import {
  FilterSectionData,
  FilterSectionDisplayData,
} from "@shared/features/filters/types";
import { useDispatch, useSelector } from "react-redux";
import { toggleArrayItem } from "@shared/utils/arrays";
import { ICON_TYPES } from "@shared/common/constants";
import {
  FILTER_TYPES_DATA,
  SIGNAL_STATUS_TO_DISPLAY,
} from "@shared/features/filters/constants";
import {
  CHECKBOX_FILTER_TYPES,
  CheckboxFilterType,
  EntryZoneCategory,
  FiltersActions,
  FiltersData,
  FilterSectionType,
  FiltersSelector,
  FilterType,
  RANGE_FILTER_TYPES,
} from "@shared/types";
import {
  getChipPreviewText,
  getIsAllSelected,
  isNumberArray,
} from "@shared/features/filters/utils";
import { max, min } from "lodash";
import { useMemo } from "react";
import { sortByStringProperty } from "@shared/utils/general";
import { isStringsArray } from "@shared/types/utils";
import { useClientsData } from "@shared/utils/hooks/misc";

export const useFilter = <T extends FilterType>({
  filterType,
  filtersActions,
  filtersSelectors,
  isMobile = false,
}: {
  filterType: T;
  filtersActions: FiltersActions;
  filtersSelectors: FiltersSelector;
  isMobile?: boolean;
}): FilterSectionData<T> => {
  const dispatch = useDispatch();
  const filtersSliceData = useSelector(filtersSelectors.selectFilters);
  const { filters, config, mappings } = filtersSliceData;
  const { getAccountByName } = useClientsData();

  const filterData = filters[filterType];
  const filterSectionType = FILTER_TYPES_DATA[filterType].filterSectionType;
  const setFunctionName =
    FILTER_TYPES_DATA[filterType as FilterType].setActionName;
  const setFilterValue = setFunctionName && filtersActions[setFunctionName];

  let clientToExchangeMapping: Record<string, string> = {};
  Object.entries(mappings.exchangeToClientsMapping || {}).forEach(
    ([exchangeName, clients]) => {
      clients?.forEach((clientName) => {
        clientToExchangeMapping[clientName] = exchangeName;
      });
    }
  );

  const toggleItem: FilterSectionData<T>["toggleItem"] = (value) => {
    if (setFilterValue) {
      dispatch(setFilterValue(toggleArrayItem(filterData?.value || [], value)));
    }
  };

  const onChange: FilterSectionData<T>["onChange"] = (value) => {
    if (setFilterValue) {
      dispatch(setFilterValue(value as any));
    }
  };

  const filterValue = filterData?.value;
  const rawFilterOptions =
    filterData && "options" in filterData ? filterData.options : [];
  const filterOptions = useMemo(() => {
    const shouldSort =
      isStringsArray(rawFilterOptions) &&
      filterType !== FilterType.AMOUNT_TYPE &&
      (!isMobile || filterType !== FilterType.SYMBOLS);
    if (shouldSort) {
      return rawFilterOptions.slice().sort((a, b) => {
        const stringSort = sortByStringProperty(a, b);
        if (filterType === FilterType.CLIENTS) {
          const clientA = getAccountByName(a);
          const clientB = getAccountByName(b);
          return (
            +!!clientB?.is_valid - +!!clientA?.is_valid ||
            sortByStringProperty(a, b)
          );
        }
        return stringSort;
      });
    }
    return rawFilterOptions;
  }, [rawFilterOptions]);

  const getIsSelected: FilterSectionData<T>["getIsSelected"] = (item) => {
    if (
      CHECKBOX_FILTER_TYPES.includes(filterType as CheckboxFilterType) &&
      Array.isArray(filterValue) &&
      filterValue
    ) {
      return (filterValue as string[]).includes(item as string);
    }
    return false;
  };

  const selectAll = () => {
    dispatch(filtersActions.selectAll([filterType] as any));
  };

  const unselectAll = () => {
    if (
      CHECKBOX_FILTER_TYPES.includes(filterType as CheckboxFilterType) &&
      Array.isArray(filterValue) &&
      filterValue
    ) {
      onChange([]);
    }
  };

  const displayData: FilterSectionDisplayData<T> = {};
  if (filterType === FilterType.EXCHANGES) {
    (
      displayData as FilterSectionDisplayData<FilterType.EXCHANGES>
    ).getIconProps = (exchange) => {
      return {
        iconType: ICON_TYPES.EXCHANGE,
        coinOrExchangeName: exchange,
      };
    };
    if (config.filterTypes?.includes(FilterType.CLIENTS)) {
      (
        displayData as FilterSectionDisplayData<FilterType.CLIENTS>
      ).getExtraInfo = (exchange: string) => {
        const numExchangeClients =
          mappings.exchangeToClientsMapping?.[exchange]?.length || 0;
        return `${numExchangeClients} Client${
          numExchangeClients > 1 ? "s" : ""
        }`;
      };
    }
  } else if (filterType === FilterType.CLIENTS) {
    (displayData as FilterSectionDisplayData<FilterType.CLIENTS>).getIconProps =
      (client: string) => {
        return {
          iconType: ICON_TYPES.EXCHANGE,
          coinOrExchangeName: clientToExchangeMapping[client],
        };
      };
    (displayData as FilterSectionDisplayData<FilterType.CLIENTS>).getExtraInfo =
      (client: string) => {
        return clientToExchangeMapping[client];
      };
    (displayData as FilterSectionDisplayData<FilterType.CLIENTS>).getAccountId =
      (client: string) => {
        return getAccountByName(client)?.client_id;
      };
  } else if (filterType === FilterType.SYMBOLS) {
    (displayData as FilterSectionDisplayData<FilterType.SYMBOLS>).getIconProps =
      (symbol: string) => {
        return {
          iconType: ICON_TYPES.COIN,
          coinOrExchangeName: symbol,
        };
      };
  } else if (
    filterType === FilterType.GROUPS ||
    filterType === FilterType.MULTI_GROUPS
  ) {
    (displayData as FilterSectionDisplayData<FilterType.GROUPS>).getIconProps =
      (id: number) => {
        const groupData = (filterData as FiltersData[FilterType.GROUPS])
          ?.groupIdToData?.[id];
        return {
          iconLink: groupData?.image,
          coinOrExchangeName: groupData?.name,
        };
      };
    (displayData as FilterSectionDisplayData<FilterType.GROUPS>).getDisplay = (
      id: number
    ) => {
      const groupData = (filterData as FiltersData[FilterType.GROUPS])
        ?.groupIdToData?.[id];
      return groupData?.name;
    };
  } else if (filterType === FilterType.SIGNAL_STATUS) {
    (
      displayData as FilterSectionDisplayData<FilterType.SIGNAL_STATUS>
    ).getDisplay = (category: EntryZoneCategory) => {
      return SIGNAL_STATUS_TO_DISPLAY[category];
    };
  }

  const title = FILTER_TYPES_DATA[filterType].title || "";
  const allSelected = getIsAllSelected(filterType, filterData);

  const getChipsText = () =>
    getChipPreviewText(filterType, filterData, isMobile);

  const shouldHide =
    (RANGE_FILTER_TYPES as ReadonlyArray<FilterType>).includes(filterType) &&
    filterType !== FilterType.TIME_FRAME &&
    isNumberArray(filterOptions) &&
    min(filterOptions as number[]) === max(filterOptions as number[]);

  const getResetButtonData = () => {
    const shouldHideResetButton = [
      FilterType.GROUPS,
      FilterType.IS_HOT,
    ].includes(filterType);
    const isResetFilterType = [
      FilterSectionType.PRICE_RANGE,
      FilterSectionType.TIME_RANGE,
      FilterSectionType.SLIDER,
      FilterSectionType.TAGS,
    ].includes(filterSectionType);
    let resetText = "Select All";
    let onClick = selectAll;
    if (isResetFilterType) resetText = "Reset";
    else if (allSelected) {
      resetText = "Clear All";
      onClick = unselectAll;
    }

    return {
      shouldHide: shouldHideResetButton,
      resetText,
      onClick,
    };
  };

  return {
    filterSectionType,
    filterType,
    title,
    options: filterOptions,
    value: filterValue ?? [],
    toggleItem,
    onChange,
    displayData,
    getIsSelected,
    selectAll,
    unselectAll,
    allSelected,
    getChipsText,
    shouldHide,
    resetButtonData: getResetButtonData(),
  };
};
